import { Box } from '@mui/material';
import { MuiButtonBase } from '@ui/base_library';
import { makeStyles } from 'tss-react/mui';
import React, { CSSProperties, useCallback } from 'react';
import useStudioAvatarModal from '@avatar/hooks/useStudioAvatarModal';
import AvatarWithFallback from '@avatar/components/AvatarWithFallback';
import Typography from '@ui/Typography';
import type { Actor } from '@avatar/types';
import {
  ActorVariant,
  StudioProjectVersion,
  TtsVersionSelectOption,
} from '../../../../server/graphql/__generated.types';
import {
  EventName,
  useEventsContext,
} from '../../../context/events/EventsContext';
import VisuallyHidden from '../../VisuallyHidden';
import { variantDisplayText } from '../utils/avatar';
import ActiveVariantVersionSelectorWrapper from './ActiveVariantVersionSelector';
import StudioClipActionTooltip from '../../StudioClip/Actions/StudioClipActionTooltip';
import { ActorsListV2PromptProps } from './modals/AvatarModal/AvatarModalPrompt';
import SingleSelect from '../../DropdownMenu/menus/SingleSelect';

export interface ActiveActorToggleProps
  extends Pick<ActorsListV2PromptProps, 'tags'> {
  actor: Actor;
  activeVariant: ActorVariant;
  actorAvatarClickDisabled?: boolean;
  activeVariantVersion: TtsVersionSelectOption;
  setActiveActor: (actor: Actor, actorVariant?: ActorVariant) => void;
  onActorAvatarClick?: () => void;
  onVariantClick: (variant: ActorVariant, modelVersion?: string) => void;
  isScriptMode: boolean;
  projectVersion: StudioProjectVersion;
  canUpdateAvatarVariantVersion?: boolean;
}

const useStyles = makeStyles()(theme => ({
  heading: {
    marginRight: theme.spacing(2),
  },
  voiceStyleDropdown: {
    width: 180,
  },
  versionSelectorWrapper: {
    marginLeft: theme.spacing(2),
    width: 128,
    [theme.breakpoints.down('md')]: {
      marginLeft: 'unset',
      marginTop: theme.spacing(2),
      width: '100%',
    },
  },
  avatarIconButton: {
    '&:focus, &.Mui-focusVisible': {
      ...(theme.mixins.focusOutline as CSSProperties),
      borderRadius: '50%',
    },
  },
}));

export default function ActiveActorToggle({
  actor,
  activeVariant,
  onActorAvatarClick = () => {},
  setActiveActor,
  actorAvatarClickDisabled,
  onVariantClick,
  activeVariantVersion,
  isScriptMode,
  projectVersion,
  canUpdateAvatarVariantVersion = true,
  tags,
}: ActiveActorToggleProps) {
  const { classes } = useStyles();
  const [eventTracking] = useEventsContext();

  const handleVariantClick = useCallback(
    (variant: ActorVariant) => {
      onVariantClick(variant);
      eventTracking.track({
        type: EventName.ActorVariantChange,
        data: {
          actorId: `${actor.id}`,
          variantId: `${variant.id}`,
          actorName: actor.name,
          variantTypeId: variant.variantType.id,
        },
      });
    },
    [actor, onVariantClick, eventTracking]
  );

  const handleVariantVersionClick = useCallback(
    (variant: ActorVariant, label: string, modelVersion?: string) => {
      onVariantClick(variant, modelVersion);
      eventTracking.track({
        type: EventName.ActorVariantVersionChanged,
        data: {
          actorId: `${actor.id}`,
          variantId: `${variant.id}`,
          actorName: actor.name,
          variantTypeId: variant.variantType.id,
          versionLabel: label,
          version: modelVersion ?? variant.version,
        },
      });
    },
    [actor, onVariantClick, eventTracking]
  );

  const handleVariantTypeChange = (option: string) => {
    const optionIndex = actor.variants.findIndex(
      variant => option === variantDisplayText(variant)
    );

    if (optionIndex !== -1) {
      const variant = actor.variants[optionIndex];
      handleVariantClick(variant);
    }
  };

  const { openAvatarModal } = useStudioAvatarModal({
    setActiveAvatar: setActiveActor,
  });

  const handleAvatarImageClick = useCallback(() => {
    if (!actorAvatarClickDisabled) {
      onActorAvatarClick();
      return;
    }
    if (actorAvatarClickDisabled) {
      return;
    }
    openAvatarModal({
      event: { type: EventName.ActorSelectionPromptOpened },
      payload: {
        activeAvatarId: actor?.id,
        activeVariantId: activeVariant?.id,
        studioVersion: projectVersion,
        tags,
      },
    });
  }, [
    activeVariant?.id,
    actor?.id,
    actorAvatarClickDisabled,
    onActorAvatarClick,
    openAvatarModal,
    projectVersion,
    tags,
  ]);

  return (
    <>
      <Box display="flex" alignItems="center">
        <Box marginRight={2}>
          <StudioClipActionTooltip
            title="Open avatar selection modal"
            placement="bottom"
            enterDelay={750}
          >
            <MuiButtonBase
              className={classes.avatarIconButton}
              onClick={handleAvatarImageClick}
              aria-label="Click to see all avatars."
            >
              <AvatarWithFallback src={actor.image} />
            </MuiButtonBase>
          </StudioClipActionTooltip>
        </Box>
        <Typography variant="pLg" bold className={classes.heading}>
          <VisuallyHidden>{`Text spoken by`}</VisuallyHidden>
          {actor.name}
        </Typography>
      </Box>
      {isScriptMode && (
        <Box display="flex" alignItems="center">
          <Box className={classes.voiceStyleDropdown}>
            <SingleSelect
              ariaLabel={'select voice style'}
              value={variantDisplayText(activeVariant)}
              items={actor.variants.map(variant => ({
                label: variantDisplayText(variant),
                value: variantDisplayText(variant),
              }))}
              onChange={item => handleVariantTypeChange(item.label)}
              openLabel="Select Voice Style"
            />
          </Box>
          {canUpdateAvatarVariantVersion && (
            <ActiveVariantVersionSelectorWrapper
              activeVariant={activeVariant}
              activeVariantVersion={activeVariantVersion}
              setActiveVariantVersion={handleVariantVersionClick}
              className={classes.versionSelectorWrapper}
            />
          )}
        </Box>
      )}
    </>
  );
}
